import { useCallback, useState } from 'react';
import { Route, Routes } from 'react-router';
import { Trans, useLingui } from '@lingui/react/macro';

import { EmptyState, IllustrationNoResults } from '@/components/EmptyState';
import { TableQueryWrapper } from '@/components/TableQueryWrapper/TableQueryWrapper';
import InputSearch from '@/design_system/InputSearch';
import { PaginationFooter } from '@/design_system/Pagination/Pagination';
import Stack from '@/design_system/Stack';
import { Body, Cell, Column, Header, Row, Table } from '@/design_system/Table/Table';
import Paragraph from '@/design_system/Typography/Paragraph';
import { Company, useCompanies } from '@/models/company';
import { getCountryName } from '@/utils/country';
import useDebouncedState from '@/utils/useDebouncedState';

import { Company as CompanyPage } from './Company/Company';
import { InvoiceDataCell } from './components/InvoiceDataCell';
import { STRIPE_STATUS_LABELS } from './stripe-status-labels';

const COMPANIES_PER_PAGE = 20;

export const CompaniesRoutes = () => {
  return (
    <Routes>
      <Route path="" element={<Companies />} />
      <Route path="/:id/*" element={<CompanyPage />} />
    </Routes>
  );
};

const Companies = () => {
  const { t } = useLingui();
  const [page, setPage] = useState(1);
  const resetPage = useCallback(() => setPage(1), []);
  const [search, debouncedSearch, debouncedSetSearch] = useDebouncedState('', 300, resetPage);

  const {
    data: { companies = [], meta } = { companies: [] },
    isLoading: isCompaniesLoading,
    isPlaceholderData: isCompaniesPlaceholder,
    isError: isCompaniesError,
  } = useCompanies(
    {
      limit: COMPANIES_PER_PAGE,
      offset: (page - 1) * COMPANIES_PER_PAGE,
      search: debouncedSearch || undefined,
      external: false,
    },
    {
      keepPreviousData: true,
    }
  );

  return (
    <main style={{ gap: '1rem' }}>
      <InputSearch
        ariaLabel={t({
          id: 'settings.organization.invoicing.companies.search.label',
          message: 'Search companies',
        })}
        placeholder={t({
          id: 'settings.organization.invoicing.companies.search.placeholder',
          message: 'Search companies...',
        })}
        value={search}
        onChange={debouncedSetSearch}
      />
      <TableQueryWrapper
        isLoading={isCompaniesLoading || isCompaniesPlaceholder}
        isError={isCompaniesError}
      >
        {companies.length === 0 ? (
          <EmptyState
            subtitle={
              <Stack>
                <Paragraph>
                  <Trans id="settings.organization.invoicing.companies.no-results.1">
                    No results are matching your criteria.
                  </Trans>
                </Paragraph>
                <Paragraph>
                  <Trans id="settings.organization.invoicing.companies.no-results.2">
                    Please try with different keywords.
                  </Trans>
                </Paragraph>
              </Stack>
            }
            illustration={<IllustrationNoResults />}
          />
        ) : (
          <Table columnWidths={['1fr', '2fr', '1fr']} bordered>
            <Header>
              <Row>
                <Column>
                  <Trans id="settings.organization.invoicing.companies.table.header.name">
                    Name
                  </Trans>
                </Column>
                <Column>
                  <Trans id="settings.organization.invoicing.companies.table.header.invoice-data">
                    Invoicing data
                  </Trans>
                </Column>
                <Column>
                  <Trans id="settings.organization.invoicing.companies.table.header.additional-info">
                    Additional info
                  </Trans>
                </Column>
              </Row>
            </Header>

            <Body>
              {companies.map((company) => (
                <CompanyRow key={company.id} company={company} />
              ))}
            </Body>

            <PaginationFooter
              itemsPerPage={COMPANIES_PER_PAGE}
              page={page}
              onPageChange={setPage}
              count={meta?.count}
            />
          </Table>
        )}
      </TableQueryWrapper>
    </main>
  );
};

const CompanyRow = ({ company }: { company: Company }) => {
  const { t } = useLingui();
  const countryName = company.country ? (getCountryName(company.country) ?? company.country) : '-';

  return (
    <Row to={`/settings_beta/billing/companies/${company.id}`}>
      <Cell isLink>
        <Paragraph weight="medium">{company.name}</Paragraph>
      </Cell>
      <Cell>
        {company.invoiceData ? (
          <InvoiceDataCell invoiceData={company.invoiceData} />
        ) : (
          <Paragraph size="200">—</Paragraph>
        )}
      </Cell>
      <Cell>
        <Stack gap="0.25rem">
          <Paragraph size="200">
            <Trans id="settings.organization.invoicing.companies.table.cell.country">
              Country: {countryName}
            </Trans>
          </Paragraph>
          {company.stripeAccountStatus !== 'none' && (
            <Paragraph size="200">
              <Trans id="settings.organization.invoicing.companies.table.cell.stripe-status">
                Stripe status: {t(STRIPE_STATUS_LABELS[company.stripeAccountStatus])}
              </Trans>
            </Paragraph>
          )}
        </Stack>
      </Cell>
    </Row>
  );
};
